"use client";

import { devLabel, devLabelPanel } from "@/lib/dev-labels";
import styles from "./ExportModal.module.css";

/**
 * BitrateControls — Phase 25-AB Task 12 (panel 21-C).
 *
 * Mockup source: docs/mockups/export.html lines 573-595.
 * Quality preset pills + bitrate select + encoding mode + framerate.
 */

export type QualityPreset = "720p" | "1080p" | "4k";
export type BitratePreset = "8m" | "12m" | "20m";
export type EncodingMode = "re-encode" | "copy";
export type FrameRate = "source" | "30" | "60";

export interface BitrateConfig {
	quality: QualityPreset;
	bitrate: BitratePreset;
	encoding: EncodingMode;
	fps: FrameRate;
}

export const DEFAULT_BITRATE: BitrateConfig = {
	quality: "1080p",
	bitrate: "12m",
	encoding: "re-encode",
	fps: "source",
};

export interface BitrateControlsProps {
	value: BitrateConfig;
	onChange: (v: BitrateConfig) => void;
}

const QUALITY_LABEL: Record<QualityPreset, string> = {
	"720p": "720p",
	"1080p": "1080p",
	"4k": "4K",
};

export function BitrateControls({ value, onChange }: BitrateControlsProps) {
	function patch<K extends keyof BitrateConfig>(key: K, v: BitrateConfig[K]) {
		onChange({ ...value, [key]: v });
	}

	return (
		<div
			className={styles.rightBlock}
			data-testid="bitrate-controls"
			{...devLabelPanel("21-C")}
		>
			<div className={styles.rightTitle}>화질 / 인코딩</div>

			<div className={styles.optRow} {...devLabel("21-C1")}>
				<div className={styles.optLabelCol}>
					<span className={styles.optLabel}>화질 프리셋</span>
					<span className={styles.optSub}>기본값 — 1080p</span>
				</div>
				<div className={styles.qualityPills} role="radiogroup" aria-label="화질 프리셋">
					{(["720p", "1080p", "4k"] as QualityPreset[]).map((q) => (
						<button
							type="button"
							key={q}
							role="radio"
							aria-checked={value.quality === q}
							className={[
								styles.qp,
								value.quality === q ? styles.qpOn : "",
								q === "4k" ? styles.qpPro : "",
							]
								.filter(Boolean)
								.join(" ")}
							onClick={() => patch("quality", q)}
							data-testid={`quality-${q}`}
						>
							{QUALITY_LABEL[q]}
						</button>
					))}
				</div>
			</div>

			<div className={styles.optRow} {...devLabel("21-C2")}>
				<div className={styles.optLabelCol}>
					<span className={styles.optLabel}>비트레이트</span>
					<span className={styles.optSub}>고품질 H.264</span>
				</div>
				<select
					className={styles.select}
					value={value.bitrate}
					onChange={(e) => patch("bitrate", e.target.value as BitratePreset)}
					data-testid="bitrate-select"
				>
					<option value="8m">표준 8 Mbps</option>
					<option value="12m">고품질 12 Mbps</option>
					<option value="20m">최고 20 Mbps</option>
				</select>
			</div>

			<div className={styles.optRow} {...devLabel("21-C3")}>
				<div className={styles.optLabelCol}>
					<span className={styles.optLabel}>인코딩 모드</span>
					<span className={styles.optSub}>copy = 빠름 / re-encode = 정확</span>
				</div>
				<select
					className={styles.select}
					value={value.encoding}
					onChange={(e) => patch("encoding", e.target.value as EncodingMode)}
					data-testid="encoding-select"
				>
					<option value="re-encode">Re-encode (고품질)</option>
					<option value="copy">Copy codec (빠름)</option>
				</select>
			</div>

			<div className={styles.optRow} {...devLabel("21-C4")}>
				<div className={styles.optLabelCol}>
					<span className={styles.optLabel}>프레임 레이트</span>
					<span className={styles.optSub}>원본 60fps 유지</span>
				</div>
				<select
					className={styles.select}
					value={value.fps}
					onChange={(e) => patch("fps", e.target.value as FrameRate)}
					data-testid="fps-select"
				>
					<option value="source">원본 유지</option>
					<option value="30">30 fps</option>
					<option value="60">60 fps</option>
				</select>
			</div>
		</div>
	);
}
